import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Alert, Badge, Button, type BadgeTone } from '@/shared/ui';
import type { CalendarPrimaryLegResponse } from '@/api/generated/schemas';
import type { CalendarEvent, CalendarStatus } from '../calendarModel';

const STATUS_TONE: Record<CalendarStatus, BadgeTone> = {
  draft: 'draft',
  contracted: 'warning',
  confirmed: 'success',
  canceled: 'danger',
  unknown: 'neutral',
};

function LegRow({ leg }: { leg: CalendarPrimaryLegResponse }) {
  const DirectionIcon = leg.direction === 'Outbound' ? ArrowRight : ArrowLeft;
  return (
    <div className="gap-phx-2 text-phx-sm flex items-center py-0.5">
      <DirectionIcon className="text-phx-text-muted h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="text-phx-text min-w-0 truncate">{leg.regularTransportName}</span>
      {leg.facilityCode && (
        <Badge tone="neutral" appearance="outline">
          {leg.facilityCode}
        </Badge>
      )}
      {leg.facilityName && (
        <span className="text-phx-text-muted min-w-0 truncate">{leg.facilityName}</span>
      )}
    </div>
  );
}

interface TermDrawerProps {
  event: CalendarEvent;
  onClose: () => void;
}

/**
 * Pravý panel s rozpadem termínu: hlavička (název, rozsah, noci, stav, kapacita)
 * + blok hlavní dopravy (primary legy a generované možnosti), nebo upozornění,
 * že doprava zatím není naplánovaná.
 */
export function TermDrawer({ event, onClose }: TermDrawerProps) {
  const { t, i18n } = useTranslation('planner');

  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  const schema = event.schema;
  const outboundLegs = schema?.primaryLegs.filter((leg) => leg.direction === 'Outbound') ?? [];
  const inboundLegs = schema?.primaryLegs.filter((leg) => leg.direction === 'Inbound') ?? [];
  const hasTransport = (schema?.primaryLegs.length ?? 0) > 0;

  return (
    <aside
      className="border-phx-border bg-phx-surface shadow-phx-lg absolute inset-y-0 right-0 z-20 flex w-95 max-w-full flex-col border-l"
      data-testid="calendar-term-drawer"
    >
      <div className="border-phx-border gap-phx-2 flex items-start border-b p-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-phx-lg text-phx-text truncate font-semibold">{event.name}</h3>
          <p className="text-phx-sm text-phx-text-muted">
            {dateFormat.format(event.start)} – {dateFormat.format(event.end)} ·{' '}
            {t('calendar.bar.nights', { count: event.nights })}
          </p>
          <div className="gap-phx-2 mt-1.5 flex flex-wrap items-center">
            <Badge tone={STATUS_TONE[event.status]}>
              {event.status === 'unknown' ? event.statusRaw : t(`calendar.status.${event.status}`)}
            </Badge>
            <Badge tone="neutral" appearance="outline">
              {t(`calendar.transport.${event.transport}`)}
            </Badge>
            {event.capacity != null && (
              <span className="text-phx-sm text-phx-text-muted">
                {t('calendar.drawer.capacity', { count: event.capacity })}
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          aria-label={t('calendar.drawer.close')}
          data-testid="calendar-drawer-close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="gap-phx-4 flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
        {!hasTransport ? (
          <Alert severity="info" title={t('calendar.drawer.noTransportTitle')}>
            {t('calendar.drawer.noTransportDescription')}
          </Alert>
        ) : (
          <>
            <section>
              <h4 className="text-phx-xs text-phx-text-2 mb-1 font-semibold tracking-wide uppercase">
                {t('calendar.drawer.outbound')}
              </h4>
              {outboundLegs.length > 0 ? (
                outboundLegs.map((leg) => <LegRow key={leg.legId} leg={leg} />)
              ) : (
                <p className="text-phx-sm text-phx-text-muted italic">
                  {t('calendar.drawer.noLegs')}
                </p>
              )}
            </section>
            <section>
              <h4 className="text-phx-xs text-phx-text-2 mb-1 font-semibold tracking-wide uppercase">
                {t('calendar.drawer.inbound')}
              </h4>
              {inboundLegs.length > 0 ? (
                inboundLegs.map((leg) => <LegRow key={leg.legId} leg={leg} />)
              ) : (
                <p className="text-phx-sm text-phx-text-muted italic">
                  {t('calendar.drawer.noLegs')}
                </p>
              )}
            </section>
            <section>
              <h4 className="text-phx-xs text-phx-text-2 mb-1 font-semibold tracking-wide uppercase">
                {t('calendar.drawer.routeOptions')}
              </h4>
              {schema && schema.routeOptions.length > 0 ? (
                schema.routeOptions.map((option) => (
                  <div
                    key={option.routeOptionId}
                    className="text-phx-sm text-phx-text gap-phx-2 flex items-center py-0.5"
                  >
                    <span className="truncate">
                      {option.outboundStationName} ⇄ {option.inboundStationName}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-phx-sm text-phx-text-muted italic">
                  {t('calendar.drawer.noRouteOptions')}
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </aside>
  );
}
